import { supabase } from '../../lib/supabase';
import { 
  Drill, 
  DrillStep
} from '../../types';

// Save generated drills to the database (REPLACES ALL EXISTING DRILLS)
// Use this for AI-generated practice plans that should replace everything
export const saveDrills = async (practiceId: string, drills: Drill[]): Promise<void> => {
  try {
    if (!practiceId || !drills || drills.length === 0) {
      throw new Error('Missing practice ID or drills');
    }

    console.log(`[saveDrills] Starting to save ${drills.length} drills for practice ${practiceId}`);

    // First, clear existing drills for this practice to start fresh
    console.log('[saveDrills] Clearing existing drills...');
    const { error: clearError } = await supabase
      .from('drills')
      .delete()
      .eq('practice_id', practiceId);
    
    if (clearError) {
      console.error('[saveDrills] Error clearing existing drills:', clearError);
      throw clearError;
    }

    // Process each drill
    for (let i = 0; i < drills.length; i++) {
      const drill = drills[i];
      await saveSingleDrillInternal(practiceId, drill, i);
    }
    
    console.log(`[saveDrills] Successfully saved all ${drills.length} drills`);
  } catch (error) {
    console.error('[saveDrills] Error saving drills:', error);
    throw new Error('Failed to save drills');
  }
};

// Add a single drill without affecting existing drills (PRESERVES EXISTING DRILLS)
// Use this for manually adding individual drills
export const addSingleDrill = async (practiceId: string, drill: Omit<Drill, 'id'>): Promise<Drill> => {
  try {
    if (!practiceId || !drill) {
      throw new Error('Missing practice ID or drill data');
    }

    console.log(`[addSingleDrill] Adding drill "${drill.name}" to practice ${practiceId}`);

    // Get the current highest order_index for this practice
    const { data: maxOrderData } = await supabase
      .from('drills')
      .select('order_index')
      .eq('practice_id', practiceId)
      .order('order_index', { ascending: false })
      .limit(1);
    
    const nextOrderIndex = (maxOrderData?.[0]?.order_index ?? -1) + 1;
    console.log(`[addSingleDrill] Using order index: ${nextOrderIndex}`);

    // Save the drill and get its ID
    const savedDrill = await saveSingleDrillInternal(practiceId, drill, nextOrderIndex);
    
    console.log(`[addSingleDrill] Successfully added drill "${drill.name}" with ID: ${savedDrill.id}`);
    return savedDrill;
  } catch (error) {
    console.error('[addSingleDrill] Error adding drill:', error);
    throw new Error(`Failed to add drill: ${drill.name}`);
  }
};

// Update a single drill without affecting existing drills (PRESERVES EXISTING DRILLS)
// Use this for manually editing individual drills
export const updateSingleDrill = async (drill: Drill): Promise<Drill> => {
  console.log(`[updateSingleDrill] Updating drill "${drill.name}" (ID: ${drill.id})`);
  
  try {
    // Add timeout to prevent hanging requests
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Drill update timeout')), 20000); // 20 second timeout
    });

    const updatePromise = (async () => {
      // Update the main drill record
      const { error: updateError } = await supabase
        .from('drills')
        .update({
          name: drill.name,
          duration: drill.duration,
          description: drill.description,
          focus_area: drill.focus_areas?.[0] || 'Team',
          intensity_level: (drill as any).intensity_level ?? null // NULL for water breaks, or the provided value
        })
        .eq('id', drill.id);

      if (updateError) {
        console.error(`[updateSingleDrill] Error updating drill:`, updateError);
        throw updateError;
      }

      // Update related data (position groups, steps, coaching points)
      await updateDrillRelatedData(drill.id, drill);

      console.log(`[updateSingleDrill] Updated drill ${drill.name}`);
      return drill;
    })();

    // Race between the update and timeout
    return await Promise.race([updatePromise, timeoutPromise]);
  } catch (error) {
    console.error(`[updateSingleDrill] Error updating drill:`, error);
    throw new Error(`Failed to update drill: ${drill.name}`);
  }
};

// Helper function to efficiently update drill's position groups, steps, and coaching points.
// Position groups are resolved/created sequentially (they depend on each other's IDs),
// but their step+coaching-point updates run in parallel.
const updateDrillRelatedData = async (drillId: string, drill: Drill) => {
  const positionGroups = drill.position_groups || [];

  const { data: existingPositionGroups, error: pgError } = await supabase
    .from('position_groups')
    .select('id, group_name')
    .eq('drill_id', drillId);

  if (pgError) {
    console.error(`[updateDrillRelatedData] Error fetching existing position groups:`, pgError);
    throw pgError;
  }

  // Resolve or create each position group, collecting (positionGroupId, steps) pairs.
  const pgStepWork: { positionGroupId: string; steps: DrillStep[] }[] = [];

  for (const pg of positionGroups) {
    if (typeof pg === 'string') {
      const existingPg = existingPositionGroups?.find(epg => epg.group_name === pg);
      if (!existingPg) {
        const { error: insertError } = await supabase
          .from('position_groups')
          .insert({ drill_id: drillId, group_name: pg });
        if (insertError) {
          console.error(`[updateDrillRelatedData] Error inserting position group ${pg}:`, insertError);
          throw insertError;
        }
      }
    } else {
      let positionGroupId: string;
      const existingPg = existingPositionGroups?.find(epg => epg.group_name === pg.group_name);

      if (existingPg) {
        positionGroupId = existingPg.id;
      } else {
        const { data: newPg, error: insertError } = await supabase
          .from('position_groups')
          .insert({ drill_id: drillId, group_name: pg.group_name })
          .select('id')
          .single();
        if (insertError) {
          console.error(`[updateDrillRelatedData] Error inserting position group ${pg.group_name}:`, insertError);
          throw insertError;
        }
        positionGroupId = newPg.id;
      }

      pgStepWork.push({ positionGroupId, steps: pg.steps || [] });
    }
  }

  // Run all position-group step updates in parallel — different groups don't share rows.
  await Promise.all(pgStepWork.map(({ positionGroupId, steps }) =>
    updatePositionGroupSteps(positionGroupId, steps, drillId)
  ));

  // Remove position groups that are no longer in the drill.
  const updatedGroupNames = positionGroups.map(pg => typeof pg === 'string' ? pg : pg.group_name);
  const groupsToRemove = (existingPositionGroups || []).filter(epg =>
    !updatedGroupNames.includes(epg.group_name)
  );

  await Promise.all(groupsToRemove.map(async (groupToRemove) => {
    await deletePositionGroupSteps(groupToRemove.id);
    const { error: deleteError } = await supabase
      .from('position_groups')
      .delete()
      .eq('id', groupToRemove.id);
    if (deleteError) {
      console.error(`[updateDrillRelatedData] Error deleting position group ${groupToRemove.group_name}:`, deleteError);
      throw deleteError;
    }
  }));
};

// Helper function to update steps for a position group.
// Uses a single upsert for existing steps (instead of N individual UPDATEs) and
// a single delete+insert for coaching points to avoid per-row RLS join overhead.
const updatePositionGroupSteps = async (positionGroupId: string, newSteps: DrillStep[], drillId: string) => {
  const { data: existingSteps, error: stepsError } = await supabase
    .from('drill_steps')
    .select('id')
    .eq('position_group_id', positionGroupId)
    .order('order_index');

  if (stepsError) {
    console.error(`[updatePositionGroupSteps] Error fetching existing steps:`, stepsError);
    throw stepsError;
  }

  const existingStepIds = new Set((existingSteps || []).map(s => s.id));
  const incomingStepIds = new Set(newSteps.map(s => s.id).filter(Boolean) as string[]);

  const stepsToDelete = [...existingStepIds].filter(id => !incomingStepIds.has(id));

  const stepsWithIndex = newSteps.map((step, index) => ({ step, index }));
  const newStepEntries = stepsWithIndex.filter(({ step }) => !step.id);
  const existingStepEntries = stepsWithIndex.filter(({ step }) => !!step.id);

  const stepsToInsert = newStepEntries.map(({ step, index }) => ({
    drill_id: drillId,
    position_group_id: positionGroupId,
    name: step.name,
    setup: step.setup || '',
    execution: step.execution || '',
    equipment: step.equipment || '',
    video_url: step.video_url || '',
    order_index: index
  }));

  // Single upsert payload for all existing steps — replaces N individual PATCH calls.
  const stepsToUpsert = existingStepEntries.map(({ step, index }) => ({
    id: step.id!,
    drill_id: drillId,
    position_group_id: positionGroupId,
    name: step.name,
    setup: step.setup || '',
    execution: step.execution || '',
    equipment: step.equipment || '',
    video_url: step.video_url || '',
    order_index: index
  }));

  try {
    // 1. Remove deleted steps (explicit coaching-point delete first; cascade may not fire via RLS).
    if (stepsToDelete.length > 0) {
      const { error: deleteCPError } = await supabase
        .from('drill_coaching_points')
        .delete()
        .in('drill_step_id', stepsToDelete);
      if (deleteCPError) throw deleteCPError;

      const { error: deleteStepsError } = await supabase
        .from('drill_steps')
        .delete()
        .in('id', stepsToDelete);
      if (deleteStepsError) throw deleteStepsError;
    }

    // 2. Upsert all existing steps in one call (was N sequential UPDATEs).
    if (stepsToUpsert.length > 0) {
      const { error: upsertError } = await supabase
        .from('drill_steps')
        .upsert(stepsToUpsert, { onConflict: 'id' });
      if (upsertError) {
        console.error(`[updatePositionGroupSteps] Error upserting steps:`, upsertError);
        throw upsertError;
      }
    }

    // 3. Replace coaching points for existing steps: 1 DELETE + 1 INSERT (was N×2 calls).
    if (existingStepEntries.length > 0) {
      const existingIds = existingStepEntries.map(({ step }) => step.id!);
      const { error: deleteCPError } = await supabase
        .from('drill_coaching_points')
        .delete()
        .in('drill_step_id', existingIds);
      if (deleteCPError) throw deleteCPError;

      const cpToInsert = existingStepEntries.flatMap(({ step }) =>
        (step.coaching_points || []).map((point, i) => ({
          drill_step_id: step.id!,
          point,
          order_index: i
        }))
      );
      if (cpToInsert.length > 0) {
        const { error: insertCPError } = await supabase
          .from('drill_coaching_points')
          .insert(cpToInsert);
        if (insertCPError) throw insertCPError;
      }
    }

    // 4. Insert new steps, then batch-insert their coaching points.
    if (stepsToInsert.length > 0) {
      const { data: insertedSteps, error: insertError } = await supabase
        .from('drill_steps')
        .insert(stepsToInsert)
        .select('id, order_index');
      if (insertError) {
        console.error(`[updatePositionGroupSteps] Error inserting new steps:`, insertError);
        throw insertError;
      }

      const cpToInsert = newStepEntries.flatMap(({ step }, i) =>
        (step.coaching_points || []).map((point, pi) => ({
          drill_step_id: insertedSteps![i].id,
          point,
          order_index: pi
        }))
      );
      if (cpToInsert.length > 0) {
        const { error: insertCPError } = await supabase
          .from('drill_coaching_points')
          .insert(cpToInsert);
        if (insertCPError) throw insertCPError;
      }
    }
  } catch (error) {
    console.error(`[updatePositionGroupSteps] Error in batch operations:`, error);
    throw error;
  }
};

// Helper function to delete all steps and coaching points for a position group
const deletePositionGroupSteps = async (positionGroupId: string) => {
  // Get all step IDs for this position group
  const { data: steps, error: stepsError } = await supabase
    .from('drill_steps')
    .select('id')
    .eq('position_group_id', positionGroupId);
  
  if (stepsError) {
    console.error(`[deletePositionGroupSteps] Error fetching steps for position group ${positionGroupId}:`, stepsError);
    throw stepsError;
  }
  
  // Delete coaching points for all steps in batch
  if (steps && steps.length > 0) {
    const stepIds = steps.map(step => step.id);
    const { error: deleteCoachingPointsError } = await supabase
      .from('drill_coaching_points')
      .delete()
      .in('drill_step_id', stepIds);
    
    if (deleteCoachingPointsError) {
      console.error(`[deletePositionGroupSteps] Error deleting coaching points for position group ${positionGroupId}:`, deleteCoachingPointsError);
      throw deleteCoachingPointsError;
    }
  }
  
  // Delete all steps in batch
  const { error: deleteError } = await supabase
    .from('drill_steps')
    .delete()
    .eq('position_group_id', positionGroupId);
  
  if (deleteError) {
    console.error(`[deletePositionGroupSteps] Error deleting steps for position group ${positionGroupId}:`, deleteError);
    throw deleteError;
  }
};

// Internal helper function to save a single drill
const saveSingleDrillInternal = async (practiceId: string, drill: Omit<Drill, 'id'>, orderIndex: number): Promise<Drill> => {
  console.log(`[saveSingleDrillInternal] Processing drill: ${drill.name}`);
  
  try {
    // Determine intensity_level: use provided value, or default to "Technique focused" (unless water break)
    let intensityLevel: string | null = (drill as any).intensity_level;
    if (intensityLevel === undefined) {
      // Check if it's a water break
      const drillNameLower = (drill.name || '').toLowerCase();
      const isWaterBreak = drillNameLower.includes('water') || 
                          drillNameLower.includes('hydration') || 
                          (drillNameLower.includes('break') && (drillNameLower.includes('rest') || drillNameLower.includes('hydrate')));
      intensityLevel = isWaterBreak ? null : 'Technique focused';
      console.log(`[saveSingleDrillInternal] Setting default intensity_level for "${drill.name}": ${intensityLevel}`);
    } else {
      console.log(`[saveSingleDrillInternal] Using provided intensity_level for "${drill.name}": ${intensityLevel}`);
    }
    
    // Insert the drill
    const { data: drillData, error: drillError } = await supabase
      .from('drills')
      .insert({
        practice_id: practiceId,
        name: drill.name,
        duration: drill.duration,
        description: drill.description || '',
        focus_area: drill.focus_areas?.[0] || 'Team',
        order_index: orderIndex,
        intensity_level: intensityLevel
      })
      .select('id')
      .single();
    
    if (drillError) {
      console.error(`[saveSingleDrillInternal] Error inserting drill ${drill.name}:`, drillError);
      throw drillError;
    }
    
    const drillId = drillData.id;
    console.log(`[saveSingleDrillInternal] Inserted drill ${drill.name} with ID: ${drillId}`);
    
    // Process position groups for this drill
    const positionGroups = drill.position_groups || [];
    if (positionGroups.length === 0) {
      console.log(`[saveSingleDrillInternal] No position groups for drill ${drill.name}, skipping...`);
      return { ...drill, id: drillId };
    }

    for (const pg of positionGroups) {
      if (typeof pg === 'string') {
        // Handle string position groups (create empty position group)
        const { error: pgError } = await supabase
          .from('position_groups')
          .insert({
            drill_id: drillId,
            group_name: pg
          });
        
        if (pgError) {
          console.error(`[saveSingleDrillInternal] Error inserting position group ${pg}:`, pgError);
          throw pgError;
        }
      } else {
        // Insert position group
        const { data: pgData, error: pgError } = await supabase
          .from('position_groups')
          .insert({
            drill_id: drillId,
            group_name: pg.group_name
          })
          .select('id')
          .single();
        
        if (pgError) {
          console.error(`[saveSingleDrillInternal] Error inserting position group ${pg.group_name}:`, pgError);
          throw pgError;
        }
        
        const positionGroupId = pgData.id;
        console.log(`[saveSingleDrillInternal] Inserted position group ${pg.group_name} with ID: ${positionGroupId}`);
        
        // Process steps for this position group
        const steps = pg.steps || [];
        if (steps.length === 0) {
          console.log(`[saveSingleDrillInternal] No steps for position group ${pg.group_name}, skipping...`);
          continue;
        }

        // Prepare steps for batch insert
        const stepInserts = steps.map((step, stepIndex) => ({
          drill_id: drillId,
          position_group_id: positionGroupId,
          name: step.name,
          setup: step.setup || '',
          execution: step.execution || '',
          equipment: step.equipment || '',
          video_url: step.video_url || '',
          order_index: stepIndex
        }));

        // Batch insert steps
        const { data: insertedSteps, error: stepsError } = await supabase
          .from('drill_steps')
          .insert(stepInserts)
          .select('id');
        
        if (stepsError) {
          console.error(`[saveSingleDrillInternal] Error inserting steps for position group ${pg.group_name}:`, stepsError);
          throw stepsError;
        }

        console.log(`[saveSingleDrillInternal] Inserted ${insertedSteps?.length || 0} steps for position group ${pg.group_name}`);
        
        // Process coaching points for all steps
        const allCoachingPoints: any[] = [];
        
        steps.forEach((step, stepIndex) => {
          if (step.coaching_points && step.coaching_points.length > 0) {
            const stepId = insertedSteps?.[stepIndex]?.id;
            if (stepId) {
              step.coaching_points.forEach((point, pointIndex) => {
                allCoachingPoints.push({
                  drill_step_id: stepId,
                  point,
                  order_index: pointIndex
                });
              });
            }
          }
        });

        // Batch insert coaching points if any exist
        if (allCoachingPoints.length > 0) {
          const { error: coachingPointsError } = await supabase
            .from('drill_coaching_points')
            .insert(allCoachingPoints);
          
          if (coachingPointsError) {
            console.error(`[saveSingleDrillInternal] Error inserting coaching points for position group ${pg.group_name}:`, coachingPointsError);
            throw coachingPointsError;
          }

          console.log(`[saveSingleDrillInternal] Inserted ${allCoachingPoints.length} coaching points for position group ${pg.group_name}`);
        }
      }
    }
    
    console.log(`[saveSingleDrillInternal] Successfully saved drill ${drill.name}`);
    return { ...drill, id: drillId };
  } catch (drillError) {
    console.error(`[saveSingleDrillInternal] Failed to save drill ${drill.name}:`, drillError);
    throw drillError;
  }
};

/** Ordered drill rows for timeline logic (does not load full drill graph). */
export async function listDrillsOrderMeta(
  practiceId: string
): Promise<{ id: string; name: string; order_index: number }[]> {
  const { data, error } = await supabase
    .from('drills')
    .select('id, name, order_index')
    .eq('practice_id', practiceId)
    .order('order_index', { ascending: true });
  if (error) {
    console.error('[listDrillsOrderMeta]', error);
    throw error;
  }
  return data ?? [];
}

/**
 * Insert a drill at a visual index (0 = before first drill), shifting existing `order_index` values.
 */
export async function insertPracticeDrillAtOrderSlot(
  practiceId: string,
  drill: Omit<Drill, 'id'>,
  insertVisualIndex: number
): Promise<Drill> {
  const sorted = await listDrillsOrderMeta(practiceId);
  const pos = Math.max(0, Math.min(insertVisualIndex, sorted.length));
  let targetOrder: number;
  if (sorted.length === 0) {
    targetOrder = 0;
  } else if (pos >= sorted.length) {
    targetOrder = sorted[sorted.length - 1].order_index + 1;
  } else {
    targetOrder = sorted[pos].order_index;
  }

  const byDesc = [...sorted].sort((a, b) => b.order_index - a.order_index);
  for (const row of byDesc) {
    if (row.order_index >= targetOrder) {
      const { error } = await supabase
        .from('drills')
        .update({ order_index: row.order_index + 1 })
        .eq('id', row.id);
      if (error) {
        console.error('[insertPracticeDrillAtOrderSlot] shift failed', error);
        throw error;
      }
    }
  }

  return saveSingleDrillInternal(practiceId, drill, targetOrder);
}

/**
 * Pick a sensible visual index for a new drill: after warmups/water, before cooldown when possible.
 */
export function computeDrillInsertVisualIndex(
  orderedDrillNames: string[],
  drillPhraseLower: string
): number {
  if (orderedDrillNames.length === 0) return 0;
  const names = orderedDrillNames.map((n) => n.toLowerCase());
  let i = 0;
  while (
    i < names.length &&
    (names[i].includes('warm') ||
      names[i].includes('dynamic') ||
      names[i].includes('water') ||
      names[i].includes('hydration'))
  ) {
    i += 1;
  }
  const afterWarmup = Math.min(Math.max(i, 1), names.length);
  const cooldownIdx = names.findIndex((n) => n.includes('cool'));
  const defenseCue =
    /\b(pickoff|balk|pitch|throw|catcher|steal|defense|fielding|infield|outfield|first\s*base|1st\b)\b/.test(
      drillPhraseLower
    );
  if (defenseCue && names.length > 2) {
    const mid = Math.floor(names.length * 0.35);
    const candidate = Math.max(afterWarmup, Math.min(mid, cooldownIdx === -1 ? names.length : cooldownIdx - 1));
    return Math.min(names.length, Math.max(afterWarmup, candidate));
  }
  return Math.min(names.length, afterWarmup);
}